import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Container, MobileStepper } from '@mui/material';
import { patientCrud, testCrud } from '@/services/firebase';
import { Patient, Test, Token } from '@/types/database';
import { hasDateExpire } from '@/utils/helpers';
import { FC, useEffect, useMemo, useRef } from 'react';
import { Dict } from '@ajorquera/libs/utils';
import { useSnackbar } from 'notistack';
import { Icon } from '@iconify/react';

import STEPS_FIELDS from '../Test/stepFields.json';
import SimpleLayout from './Main.layout';
import useAsync from '@/hooks/useAsync';

const TestLayoutPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const testId = id;
  const lastPath = location.pathname.split('/').pop();
  const step = Number(lastPath);
  const showStepper = !isNaN(step);
  const numberSteps = STEPS_FIELDS.length;

  const {
    loading,
    data: testData,
    error,
  } = useAsync(() => testCrud.read(id as string) as Promise<Test>, {
    inmediate: !!id,
  });

  const {
    loading: loadingPatient,
    data: patientData,
    error: errorPatient,
  } = useAsync<Patient>(() => patientCrud.read(testData?.patientId as string) as Promise<Patient>, {
    inmediate: !!testData?.patientId,
  });

  const answer = useMemo(() => testData?.answer || {}, [testData]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  const updateAnswer = (newAnswer: Dict) => {
    return testCrud.update(id as string, {
      answer: { ...answer, ...newAnswer },
    });
  };

  const { execute: update, error: errorUpdate, loading: loadingUpdate } = useAsync(updateAnswer);

  const goToStep = (step: number) => {
    if (step < 0 || step > STEPS_FIELDS.length) return;
    navigate(`./${step}`, { state: { step } });
  };

  // Redirect to home if no id or testData is not loaded
  useEffect(() => {
    if (!id || (!testData && !loading)) {
      navigate('/');
      enqueueSnackbar('Test not found or invalid', { variant: 'error' });
    }
  }, [id, testData, loading]);

  // Redirect if invitation is not found or invalid
  useEffect(() => {
    if (patientData) {
      const token = (patientData.accessTokens as unknown as Token[])?.find((token) => token.resourceId === testId);

      if (!token || hasDateExpire(token?.expiresAt.toDate() as Date)) {
        navigate('/login');
        enqueueSnackbar('Invitation expired or invalid', { variant: 'error' });
      }
    }
  }, [patientData, errorPatient, loadingPatient]);

  const context = useMemo(
    () => ({
      updateAnswer: update,
      loadingUpdate,
      errorUpdate,
      patientData,
      testData,
      data: testData?.answer,
      loading,
      testId,
      answer,
      error,
      id,
    }),
    [testData, loading, error, update, errorUpdate, loadingUpdate, id, testId, answer, patientData]
  );

  return (
    <>
      <SimpleLayout>
        <Box sx={{ flexGrow: 1 }}>
          <Outlet context={context} />
        </Box>
        <Container
          disableGutters
          maxWidth="sm"
          sx={{
            position: 'fixed',
            bottom: 0,
            borderTop: '1px solid #e0e0e0',
            backgroundColor: '#FFF',
          }}
        >
          {showStepper && (
            <MobileStepper
              variant="dots"
              steps={numberSteps}
              activeStep={step}
              position="static"
              backButton={
                <Button onClick={() => goToStep(step - 1)} disabled={step === 0}>
                  <Icon icon="mingcute:left-fill" width="24" height="24" /> Back
                </Button>
              }
              nextButton={<div style={{ width: 100 }} />}
            />
          )}
        </Container>
      </SimpleLayout>
    </>
  );
};

export default TestLayoutPage;
